import { validateCSV, CSVFieldType, type FieldSchema } from "@tellescope/utils";

/**
 * `satisfies` rather than a `Record<string, FieldSchema>` annotation, which is load-bearing for the
 * caller's types: annotating widens the keys to `string`, so `InferCSVType` can only resolve to an
 * index signature and every field read off `validateCSV`'s result loses its type. `satisfies` checks
 * each entry against `FieldSchema` just the same while keeping the keys literal.
 */
export const MEDICATION_FIELDS = {
  title: { type: CSVFieldType.STRING, required: true },
  calendarEventId: { type: CSVFieldType.STRING },
  prescribedBy: { type: CSVFieldType.STRING },
  prescribedAt: { type: CSVFieldType.TIMESTAMP },
  startedTakingAt: { type: CSVFieldType.TIMESTAMP },
  stoppedTakingAt: { type: CSVFieldType.TIMESTAMP },
  rxNormCode: { type: CSVFieldType.STRING },
  fdbCode: { type: CSVFieldType.STRING },
  source: { type: CSVFieldType.STRING },
  externalId: { type: CSVFieldType.STRING },
  notes: { type: CSVFieldType.STRING },
  orderStatus: { type: CSVFieldType.STRING },
  pharmacyName: { type: CSVFieldType.STRING },
  prescriberName: { type: CSVFieldType.STRING },
  reasonForTaking: { type: CSVFieldType.STRING },
  directions: { type: CSVFieldType.STRING },
  "dispensing.quantity": { type: CSVFieldType.NUMBER },
  "dispensing.unit": { type: CSVFieldType.STRING },
  "dosage.value": { type: CSVFieldType.STRING },
  "dosage.unit": { type: CSVFieldType.STRING },
  "dosage.quantity": { type: CSVFieldType.STRING },
  "dosage.frequency": { type: CSVFieldType.STRING },
  "dosage.frequencyDescriptor": { type: CSVFieldType.STRING },
  "dosage.description": { type: CSVFieldType.STRING },
} satisfies Record<string, FieldSchema>;

/** Upper bound on rows accepted from a single medication CSV, to keep a large export from exhausting the tab. */
export const MAX_MEDICATION_CSV_ROWS = 5000;

/** Upper bound on the size of a single medication CSV file, enforced before the file is read into memory. */
export const MAX_MEDICATION_CSV_BYTES = 5 * 1024 * 1024;

export const validateMedicationCSV = (content: string) =>
  validateCSV(content, {
    schema: MEDICATION_FIELDS,
    options: {
      allowUnknownColumns: false, // Strict mode: only allow defined fields
      continueOnError: true, // Skip invalid rows, continue processing
      maxRows: MAX_MEDICATION_CSV_ROWS,
    },
  });
